import { useEffect, useRef } from 'react'

/**
 * Atajos de teclado.
 *
 * "Un producto de trabajo que no se puede operar con teclado no es un producto
 * de trabajo." Los atajos globales del documento:
 *
 *   Ctrl+K       cambiar de tenant
 *   Ctrl+/       búsqueda global
 *   N            nueva factura
 *   Ctrl+Enter   emitir el documento abierto
 *   Esc          cerrar panel o modal
 *   ?            ver todos los atajos
 */

export interface Atajo {
  /** `k`, `Enter`, `Escape`, `?`. Se compara contra `event.key` sin distinguir mayúsculas. */
  tecla: string
  ctrl?: boolean
  shift?: boolean
  alt?: boolean
  /** Si el atajo debe funcionar también dentro de un campo de texto (Ctrl+Enter sí, `N` no). */
  dentroDeCampos?: boolean
}

export function describirAtajo(atajo: Atajo): string {
  const partes: string[] = []
  if (atajo.ctrl) partes.push('Ctrl')
  if (atajo.alt) partes.push('Alt')
  if (atajo.shift) partes.push('Shift')
  const tecla = atajo.tecla === 'Enter' ? '⏎' : atajo.tecla === 'Escape' ? 'Esc' : atajo.tecla.toUpperCase()
  partes.push(tecla)
  return partes.join('+')
}

function esCampoEditable(destino: EventTarget | null): boolean {
  if (!(destino instanceof HTMLElement)) return false
  const etiqueta = destino.tagName.toLowerCase()
  return etiqueta === 'input' || etiqueta === 'textarea' || etiqueta === 'select' || destino.isContentEditable
}

export function useAtajo(atajo: Atajo | null, accion: (evento: KeyboardEvent) => void, activo = true): void {
  // Se guarda la acción en una referencia para que pasar una función en línea
  // no vuelva a suscribir el oyente en cada render.
  const accionRef = useRef(accion)
  accionRef.current = accion

  const tecla = atajo?.tecla ?? null
  const ctrl = atajo?.ctrl ?? false
  const shift = atajo?.shift
  const alt = atajo?.alt ?? false
  const dentroDeCampos = atajo?.dentroDeCampos ?? false

  useEffect(() => {
    if (!activo || tecla === null) return

    const alPulsar = (evento: KeyboardEvent): void => {
      if (evento.key.toLowerCase() !== tecla.toLowerCase()) return
      if (ctrl !== (evento.ctrlKey || evento.metaKey)) return
      if (alt !== evento.altKey) return
      if (shift !== undefined && shift !== evento.shiftKey) return
      if (!dentroDeCampos && esCampoEditable(evento.target)) return

      evento.preventDefault()
      accionRef.current(evento)
    }

    window.addEventListener('keydown', alPulsar)
    return () => window.removeEventListener('keydown', alPulsar)
  }, [activo, tecla, ctrl, shift, alt, dentroDeCampos])
}
